import React, { useEffect, useState, useMemo } from 'react';
import {
  IoIosArrowBack,
  IoIosArrowDroprightCircle,
  IoIosArrowForward,
} from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainSlider from './MainSlider';
import CategorySelector from './CategorySelector';
import ProductList from './ProductList';
import Modal from '../../components/Modal';
import Profits from '../../profits';
import { instance } from '../../api/axiosInstance';
import { Product } from '../../types/types';
import Loading from '../../components/Loading';
import PartnerCard from '../products/modeling/PartnerCardProps';
import { NavLink } from 'react-router-dom';
import { partnersData } from '../../data/partnersData';
import PartnersList from '../products/modeling/PartnersList';
import StoreCard from '../products/modeling/StoreCard';
import { storeData } from '../../data/storeData';
import { projectsData } from '../../data/projectsData';
import ProjectCard from '../products/modeling/ProjectCard';

// Article 인터페이스 정의
interface ArticleType {
  imgURL: string;
  title: string;
  content: string;
  url: string;
}

// Helper components
// 화살표 컴포넌트: 슬라이더에 다음 항목으로 이동하는 화살표
const SampleNextArrow = (props: { className: string; onClick: () => void }) => {
  const { className, onClick } = props;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // 화면 크기 변화에 따라 모바일 여부를 설정합니다.
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767); // 767px 이하를 모바일로 간주
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // 초기 로드 시에도 모바일 여부 확인

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className={`${className} slick-next`}
      style={{
        display: 'block',
        right: isMobile ? '10px' : '10px', // Adjust the space between products and arrows
        top: isMobile ? '50%' : '190px',
        transform: isMobile ? 'translateY(-50%)' : undefined,
        zIndex: '9',
      }}
      onClick={onClick}
    >
      <div className="bg-white w-[40px] h-[40px] border border-gray-200 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center">
        <IoIosArrowForward className="text-[#727272]" size={22} />
      </div>
    </div>
  );
};

// 화살표 컴포넌트: 슬라이더에서 이전 항목으로 이동하는 화살표
const SamplePrevArrow = (props: { className: string; onClick: () => void }) => {
  const { className, onClick } = props;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // 화면 크기 변화에 따라 모바일 여부를 설정합니다.
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className={`${className} slick-prev`}
      style={{
        display: 'block',
        left: isMobile ? '10px' : '-50px', // 모바일일 경우 위치 조정
        top: isMobile ? '50%' : '190px', // 모바일일 경우 위치 조정
        transform: isMobile ? 'translateY(-50%)' : undefined,
        zIndex: '9',
      }}
      onClick={onClick}
    >
      <div className="bg-white w-[40px] h-[40px] border border-gray-200 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center">
        <IoIosArrowBack className="text-[#727272]" size={22} />
      </div>
    </div>
  );
};

// 숫자를 포맷하는 함수
const formatNumber = (number: number | bigint, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(number);

export default function Home() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>('MODELING');
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [articles, setArticles] = useState<ArticleType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]); // 제품 데이터 상태 추가
  const [popularProducts, setPopularProducts] = useState<Product[]>([]);

  // 페이지 렌더링 시 최상단으로 스크롤
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
      });
    }, 100);
  }, []);

  // 인기 상품을 가져오는 함수
  useEffect(() => {
    const fetchPopularProducts = async () => {
      try {
        const response = await instance.get<Product[]>('/api/products/filter', {
          params: {
            category: selectedCategory, // 선택한 카테고리로 필터링
            sortBy: 'rating', // 평점 기준으로 정렬
          },
        });
        setPopularProducts(response.data);
      } catch (err) {
        console.error(err);
        setError(t('home.error.popular_products'));
      }
    };

    fetchPopularProducts();
  }, [selectedCategory, t]);

  // 기사 데이터를 가져오는 함수
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await instance.get<ArticleType[]>('/api/articles');
        setArticles(response.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(t('home.error.articles'));
        setLoading(false);
      }
    };

    fetchArticles();
  }, [t]);

  // 제품 데이터를 가져오는 함수
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await instance.get<Product[]>('/api/products/filter', {
          params: { category: selectedCategory }, // 선택한 카테고리로 필터링
        });
        setProducts(response.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(t('home.error.products'));
        setLoading(false);
      }
    };

    fetchProducts();
  }, [selectedCategory, t]);

  // 총 슬라이드 수 계산
  const totalSlides = useMemo(() => articles.length, [articles]);

  // 슬라이드에 표시할 기사 데이터 변환
  const visibleSlides = useMemo(() => {
    return articles.map((article) => ({
      image: article.imgURL
        ? `${process.env.REACT_APP_API_BASE_URL}${article.imgURL}`
        : '',
      title: article.title,
      description: article.content,
      url: article.url,
    }));
  }, [articles]);

  // 모달 열기 함수
  const openModal = () => {
    setIsModalOpen(true);
  };

  // 모달 닫기 함수
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // 다음 슬라이드로 이동
  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
    );
  };

  // 이전 슬라이드로 이동
  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? totalSlides - 1 : prevIndex - 1
    );
  };

  // 선택한 카테고리의 제품 필터링
  const filteredProducts = useMemo(() => {
    return products.filter((product) => product.category === selectedCategory);
  }, [selectedCategory, products]);

  // 할인 제품 필터링
  const discountedProducts = useMemo(() => {
    return products.filter((product) => product.isDiscounted);
  }, [filteredProducts]);

  // 자동 슬라이드 전환 설정
  useEffect(() => {
    if (totalSlides === 0) return;

    const intervalId = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
      );
    }, 6000);

    return () => clearInterval(intervalId);
  }, [totalSlides]);

  // 현재 언어에 맞는 로케일 설정
  const currentLocale = useMemo(() => {
    switch (i18n.language) {
      case 'en':
        return 'en-US';
      case 'ja':
        return 'ja-JP';
      default:
        return 'ko-KR';
    }
  }, [i18n.language]);

  // 슬라이더 설정 정의
  const settings = useMemo(
    () => ({
      dots: false,
      infinite: false,
      speed: 800,
      slidesToShow: 10,
      slidesToScroll: 5,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 4, // 태블릿 화면 크기에서 보여줄 슬라이드 수
            slidesToScroll: 4,
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 3, // 모바일 화면 크기에서 보여줄 슬라이드 수
            slidesToScroll: 3,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 2, // 작은 모바일 화면에서 보여줄 슬라이드 수
            slidesToScroll: 2,
          },
        },
      ],
    }),
    []
  );

  // 로딩 상태 표시
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loader"></div>
        <span className="ml-2 text-lg">{t('home.loading')}</span>
      </div>
    );
  }

  // 에러 상태 표시
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500 text-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="relative">
      {/* 메인 슬라이더 */}
      <MainSlider
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        visibleSlides={visibleSlides}
        handleNext={handleNext}
        handlePrev={handlePrev}
      />

      <div className="max-w-[1440px] w-full mx-auto pb-[100px] mobile:pb-[48px] mobile:px-4">
        {/* 카테고리 선택 */}
        <CategorySelector
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
        {/* 인기 상품 */}
        <ProductList
          products={popularProducts}
          formatNumber={(num) => formatNumber(num, currentLocale)}
          title={t('home.popular_products')}
          productstext={t('home.productstext')}
          sub_title={t('home.popular_products_description')}
        />

        {/* 할인 상품 */}
        <ProductList
          products={discountedProducts}
          formatNumber={(num) => formatNumber(num, currentLocale)}
          title={t('home.discount_products')}
          productstext={t('home.productstext')}
          sub_title={t('home.discount_products_description')}
        />
        <div className="mb-[45px] mt-[100px] mobile:mt-[80px] flex justify-between items-center mobile:flex-col">
          {/* Text Section (similar to the one in the provided image) */}
          <div className="w-1/2 p-6 mobile:p-0 text-left mobile:w-full">
            <h2 className="text-5xl mobile:text-[28px] text-[#414042] Pretendard-Bold mb-4">
              {t('home.new_store_title')}
            </h2>
            <p
              className="text-[#414042] Pretendard-Regular text-lg my-8 mobile:my-6 mobile:text-xs mobile:leading-5"
              dangerouslySetInnerHTML={{
                __html: t('home.new_store_description'),
              }}
            ></p>

            <div className="flex space-x-4 mt-10  mobile:mt-6 ">
              <button
                className="bg-white mobile:text-xs text-[#414042] hover:bg-gray-100 transition duration-300 ease-in-out border border-[#414042] px-6 py-2 rounded-full Pretendard-Regular shadow-sm"
                onClick={() => navigate('/notice/1')}
              >
                {t('home.learn_more')}
              </button>
            </div>
          </div>

          {/* 비디오 섹션 */}
          <div className="w-full md:w-1/2 p-6 mobile:px-0 mobile:mt-4">
            <video
              className="w-full h-auto rounded-lg shadow-lg"
              autoPlay
              loop
              muted
              playsInline
              controlsList="nodownload"
              onPause={(e) => {
                const video = e.target as HTMLVideoElement;
                video.play(); // 비디오 일시 정지 방지
              }}
              src={`${process.env.PUBLIC_URL}/Render_Me.mp4`}
            >
              {t('home.video_not_supported')}
            </video>
          </div>
        </div>

        {/* 파트너 셀러 섹션 */}
        <div className="mt-[100px] mobile:mt-[60px] flex items-center justify-between pl-2 mobile:pl-0">
          <h1 className="Pretendard-Bold text-4xl mobile:text-lg text-[#414042]">
            {t('home.partner_sellers')}
          </h1>
        </div>
        <div className={'flex justify-between '}>
          <p className="mb-[25px] mt-[6px] text-base mobile:text-xs Pretendard-Regular text-[#414042] tracking-wide mobile:mt-1 pl-2 mobile:pl-0 ">
            {t('home.partner_sellers_description')}
          </p>
          <p
            className="mb-[24px] mt-[6px] mr-[10px] text-[#323232] Pretendard-Regular text-[14px] mobile:text-[15px] mobile:mt-2 flex items-center cursor-pointer hover:underline mobile:hidden"
            onClick={() => navigate('/partners')}
          >
            {t('home.view_more_partners')}
            <div className="ml-[6px] mb-[1px]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={20}
                height={20}
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="#323232"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="m10 17l5-5l-5-5"
                ></path>
              </svg>
            </div>
          </p>
        </div>

        <div className="mb-[45px] flex flex-wrap gap-4 mobile:grid mobile:grid-cols-2 ">
          {/* partnersData를 사용하여 파트너 카드 렌더링 */}
          <PartnersList />
        </div>

        <div className="mt-[100px] mobile:mt-[60px] flex items-center justify-between pl-2 mobile:pl-0">
          <h1 className="Pretendard-Bold text-4xl mobile:text-lg text-[#414042]">
            파트너
          </h1>
        </div>
        <div className={'flex justify-between '}>
          <p className="mb-[25px] mt-[6px] text-base mobile:text-xs Pretendard-Regular text-[#414042] tracking-wide mobile:mt-1 pl-2 mobile:pl-0 ">
          크릿스토어 파트너를 소개합니다.
          </p>
          <p
            className="mb-[24px] mt-[6px] mr-[10px] text-[#323232] Pretendard-Regular text-[14px] mobile:text-[15px] mobile:mt-2 flex items-center cursor-pointer hover:underline mobile:hidden"
            onClick={() => navigate('/partners')}
          >
            {t('home.view_more_partners')}
            <div className="ml-[6px] mb-[1px]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={20}
                height={20}
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="#323232"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="m10 17l5-5l-5-5"
                ></path>
              </svg>
            </div>
          </p>
        </div>
        <div className="mb-[45px] flex flex-wrap gap-4 mobile:grid mobile:grid-cols-2 ">
        {storeData.map((store) => (
        <StoreCard
          key={store.id}
          imageUrl={store.imageUrl}
          logoUrl={store.logoUrl}
          storeName={store.storeName}
          description={store.description}
          verified={store.verified}
          urls={store.urls}
        />
      ))}
        </div>


        {/* 배너 이미지 */}
        {/* <NavLink
          to="https://forms.gle/ki8Sx3Ybkw3v2gnH8"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className='pt-8'>
            <img
              src={`${process.env.PUBLIC_URL}/images/banner3.png`}
              alt="모집글"
              className="w-full h-auto object-cover rounded"
            />
          </div>
        </NavLink> */}

        {/* 모달 컴포넌트 */}
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <Profits />
        </Modal>
      </div>
    </div>
  );
}
